import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Save, X } from 'lucide-react';
import { SEVERITIES, DEFAULT_VARIABLES } from './ruleBuilderData';
import { RuleBuilderSections } from './RuleBuilderSections';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RuleBuilderDragDrop = ({ 
  rule = null, 
  versionId,
  token,
  onSave, 
  onCancel,
  variables = DEFAULT_VARIABLES,
  lang: lang = 'fr' 
}) => {
  const [ruleData, setRuleData] = useState({
    rule_key: rule?.rule_key || '',
    name: rule?.name || '',
    description: rule?.description || '',
    severity: rule?.severity || 'hard',
    priority: rule?.priority || 100,
    conditions: rule?.conditions || { all: [{ var: '', op: '==', value: '' }] },
    actions: rule?.actions || [{ action: 'block' }],
    messages: rule?.messages || { fr: '', en: '', ar: '' },
    applies_to_sectors: rule?.applies_to_sectors || null,
    applies_to_territories: rule?.applies_to_territories || null,
    applies_to_actors: rule?.applies_to_actors || null,
    is_active: rule?.is_active ?? true
  });

  const [conditionLogic, setConditionLogic] = useState(
    rule?.conditions?.all ? 'all' : 'any'
  );
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');

  const conditions = ruleData.conditions[conditionLogic] || [];

  const updateField = (field, value) => {
    setRuleData(prev => ({ ...prev, [field]: value }));
  };

  // Condition management
  const addCondition = () => {
    const newConditions = [...conditions, { var: '', op: '==', value: '' }];
    updateField('conditions', { [conditionLogic]: newConditions });
  };

  const updateCondition = (index, field, value) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    updateField('conditions', { [conditionLogic]: newConditions });
  };

  const removeCondition = (index) => {
    if (conditions.length <= 1) return;
    const newConditions = conditions.filter((_, i) => i !== index);
    updateField('conditions', { [conditionLogic]: newConditions });
  };

  const moveCondition = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= conditions.length) return;
    const newConditions = [...conditions];
    const [moved] = newConditions.splice(fromIndex, 1);
    newConditions.splice(toIndex, 0, moved);
    updateField('conditions', { [conditionLogic]: newConditions });
  };

  // Action management
  const addAction = () => {
    updateField('actions', [...ruleData.actions, { action: 'warning' }]);
  };

  const updateAction = (index, field, value) => {
    const newActions = [...ruleData.actions];
    newActions[index] = { ...newActions[index], [field]: value };
    updateField('actions', newActions);
  };

  const removeAction = (index) => {
    if (ruleData.actions.length <= 1) return;
    updateField('actions', ruleData.actions.filter((_, i) => i !== index));
  };

  // Save handler
  const handleSave = async () => {
    if (!ruleData.rule_key || !ruleData.name) {
      alert('Clé et nom de règle requis');
      return;
    }

    setSaving(true);
    try {
      const url = rule?.id 
        ? `${API_URL}/api/admin/rules/${rule.id}`
        : `${API_URL}/api/admin/rules`;
      
      const method = rule?.id ? 'PUT' : 'POST';
      
      const payload = {
        ...ruleData,
        version_id: versionId
      };

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        onSave && onSave(data.rule || data);
      } else {
        const error = await res.json();
        alert(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch {
      alert('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  // Auto-generate rule key
  const generateRuleKey = () => {
    const prefix = `R-${ruleData.severity.toUpperCase()}`;
    const num = String(Math.floor(Math.random() * 999) + 1).padStart(3, '0');
    updateField('rule_key', `${prefix}-${num}`);
  };

  const selectedSeverity = SEVERITIES.find(s => s.value === ruleData.severity);

  return (
    <div className="bg-gray-50 rounded-xl p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${selectedSeverity?.color} flex items-center justify-center text-white text-xl`}>
            {selectedSeverity?.icon}
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {rule ? 'Modifier la règle' : 'Nouvelle règle'}
            </h2>
            <p className="text-sm text-gray-500">
              {ruleData.rule_key || 'Clé auto-générée'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            <X className="w-4 h-4 mr-2" /> Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Sauvegarde...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <RuleBuilderSections
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        ruleData={ruleData}
        updateField={updateField}
        generateRuleKey={generateRuleKey}
        conditionLogic={conditionLogic}
        setConditionLogic={setConditionLogic}
        conditions={conditions}
        updateCondition={updateCondition}
        removeCondition={removeCondition}
        moveCondition={moveCondition}
        variables={variables}
        addCondition={addCondition}
        updateAction={updateAction}
        removeAction={removeAction}
        addAction={addAction}
        SEVERITIES={SEVERITIES}
      />


      {/* JSON Preview */}
      <details className="bg-gray-800 rounded-lg overflow-hidden">
        <summary className="px-4 py-2 text-white cursor-pointer hover:bg-gray-700">
          📄 Aperçu JSON
        </summary>
        <pre className="p-4 text-xs text-green-400 overflow-auto max-h-64">
          {JSON.stringify(ruleData, null, 2)}
        </pre>
      </details>
    </div>
  );
};

export default RuleBuilderDragDrop;

